/**
 * Column group definitions for the ReturnsTable.
 * Defines visual grouping, colors, and default ordering.
 */

export interface ColumnGroup {
  id: string
  label: string
  colorClass: string       // Tailwind background class for header
  headerColorClass: string // Tailwind background class for column headers (slightly different)
  columns: string[]        // Column IDs in this group
}

export const COLUMN_GROUPS: ColumnGroup[] = [
  {
    id: 'identity',
    label: 'Identity',
    colorClass: 'bg-slate-700/50',
    headerColorClass: 'bg-slate-800/30',
    columns: ['ticker', 'underlyingTicker', 'inceptionDate'],
  },
  {
    id: 'performance',
    label: 'Performance',
    colorClass: 'bg-blue-900/40',
    headerColorClass: 'bg-blue-900/20',
    columns: ['totalReturn', 'cagr'],
  },
  {
    id: 'drip',
    label: 'DRIP Scenarios',
    colorClass: 'bg-emerald-900/40',
    headerColorClass: 'bg-emerald-900/20',
    columns: ['finalValue0Percent', 'finalValue50Percent', 'finalValue', 'flatThresholdPercent'],
  },
  {
    id: 'dividends',
    label: 'Dividends',
    colorClass: 'bg-purple-900/40',
    headerColorClass: 'bg-purple-900/20',
    columns: ['dividendYield', 'dividendsReceived', 'dividendGrowthRatePercent'],
  },
  {
    id: 'risk',
    label: 'Risk',
    colorClass: 'bg-red-900/40',
    headerColorClass: 'bg-red-900/20',
    columns: ['maxDrawdownPercent', 'volatilityPercent'],
  },
  {
    id: 'price',
    label: 'Price',
    colorClass: 'bg-amber-900/40',
    headerColorClass: 'bg-amber-900/20',
    columns: ['startPrice', 'endPrice', 'priceChangeDollars', 'priceChangePercent'],
  },
]

export const DEFAULT_GROUP_ORDER = COLUMN_GROUPS.map((g) => g.id)

/**
 * Get the group that a column belongs to
 */
export function getColumnGroup(columnId: string): ColumnGroup | undefined {
  return COLUMN_GROUPS.find((group) => group.columns.includes(columnId))
}

/**
 * Get groups in the specified order, filtering to only groups that have visible columns
 */
export function getOrderedGroups(
  groupOrder: string[],
  visibleColumns: string[]
): ColumnGroup[] {
  return groupOrder
    .map((groupId) => COLUMN_GROUPS.find((g) => g.id === groupId))
    .filter((group): group is ColumnGroup => {
      if (!group) return false
      // Only include groups that have at least one visible column
      return group.columns.some((col) => visibleColumns.includes(col))
    })
}

/**
 * Get visible columns for a specific group, in their defined order
 */
export function getVisibleColumnsInGroup(
  group: ColumnGroup,
  visibleColumns: string[]
): string[] {
  return group.columns.filter((col) => visibleColumns.includes(col))
}

/**
 * Check if a column is the first visible column in its group
 */
export function isFirstColumnInGroup(
  columnId: string,
  visibleColumns: string[]
): boolean {
  const group = getColumnGroup(columnId)
  if (!group) return false

  const visibleInGroup = getVisibleColumnsInGroup(group, visibleColumns)
  return visibleInGroup.length > 0 && visibleInGroup[0] === columnId
}

/**
 * Build ordered column list based on group order and visibility
 */
export function buildOrderedColumnList(
  groupOrder: string[],
  visibleColumns: string[]
): string[] {
  const orderedColumns: string[] = []

  for (const groupId of groupOrder) {
    const group = COLUMN_GROUPS.find((g) => g.id === groupId)
    if (!group) continue

    for (const columnId of group.columns) {
      if (visibleColumns.includes(columnId)) {
        orderedColumns.push(columnId)
      }
    }
  }

  return orderedColumns
}
